import type { ScheduleEntry } from "@edu/shared";

/**
 * Дверь из карточки занятия в форму «Задать домашнюю».
 *
 * Зачем модуль. Домашка задаётся ПОСЛЕ занятия, а занятие человек в этот момент
 * держит открытым в расписании: карточка это то место, где он и стоит. До
 * 03.09.2026 из неё не вело ни одной ссылки ни на ученика, ни на его домашку, и
 * та же работа стоила ухода в раздел «Домашка», выбора ученика в списке и
 * возврата обратно.
 *
 * Три решения, и каждое закрывает свою поломку.
 *
 * 1. **Ссылка тихой строкой, а не кнопкой в ряду действий.** Ряд у карточки
 *    один: значки-инструменты слева, главное действие справа (шапка
 *    `EntryCard.tsx`). Домашка не главное действие занятия и не инструмент над
 *    ним, а переход в соседнюю работу, и в ряду она либо оттеснила бы «Войти»,
 *    либо встала бы рядом с «Отменить» на равных. Место и подпись те же, что у
 *    занятия в карточке ученика (`StudentOccurrenceRow.tsx`), а правило видимости
 *    РАЗНОЕ: там дверь показывается только при непустой заготовке урока, потому
 *    что у занятия, чью заготовку стёрла уборка, брать нечего. Здесь дверь есть
 *    и без заготовки: форма тогда открывается с выбором источника, и человеку,
 *    ведущему занятия в Zoom, это единственный путь, потому что уроков у него
 *    не заводится вовсе
 * 2. **Заготовка подставляется ТОЛЬКО когда она известна.** Формой она не
 *    угадывается: пустой `planId` в адресе означает, что человек выберет
 *    источник сам, а неверный увёл бы его в чужой материал
 * 3. **Занятие группы этой двери не получает**, разбор в `homeworkOffered`
 */

/**
 * Есть ли у занятия кому задавать домашнюю.
 *
 * ⚠️ **У сводной строки занятия группы двери нет, и это решение, а не пропуск.**
 * Домашка принадлежит УЧЕНИКУ (раздел 6 CLAUDE.md: группа это пучок пар, домашка
 * и оплата остаются на ученике), а у сводной строки ученика нет ни одного:
 * `counterpartUserId` там пустой, и состава группы строка не несёт вовсе, в ней
 * только название и число участников. Ссылка отсюда привела бы в форму с пустым
 * списком получателей, то есть не подставила бы ни ученика, ни заготовку, и
 * сэкономила бы ровно ноль: получателей человек всё равно отмечал бы руками, как
 * из раздела «Домашка». Задать её нескольким сразу форма умеет, и путь к этому
 * лежит через состав группы, а не через строку расписания, которая состава не
 * знает. Осиротевшего урока занятия группы это не касается: там строка обычная,
 * с одним учеником, и дверь у неё есть.
 *
 * ⚠️ Ученику двери нет: заготовки это материал преподавателя, и все ручки
 * заготовок отвечают ученику 404.
 *
 * ⚠️ У отменённого и уехавшего занятия двери тоже нет: домашка задаётся после
 * занятия, а этих занятий не было. У уехавшего она вдобавок стояла бы дважды,
 * на старом месте и на новом.
 */
export function homeworkOffered(entry: ScheduleEntry): boolean {
  if (entry.myRole !== "teacher" || entry.counterpartUserId === null) return false;
  return entry.state !== "cancelled" && entry.state !== "moved_away";
}

/**
 * Заготовка занятия.
 *
 * ⚠️ Значение приезжает УЖЕ РАЗРЕШЁННЫМ (`ScheduleEntry.planId`): лестницу
 * «урок, иначе правка занятия, иначе заготовка серии» считает сервер, и клиент
 * её не повторяет. До 03.09.2026 здесь стояла правка занятия, а остальное
 * доспрашивалось у урока отдельным запросом на каждое открытие карточки: в
 * строке сетки заготовки не лежало вовсе.
 *
 * ⚠️ Отсутствие ключа и `null` в нём значат разное («мы этого не сказали»
 * против «заготовки у занятия нет»), но дверь в домашку они закрывают
 * одинаково: подставлять в форму нечего, и человек выберет источник сам.
 */
export function planOfEntry(entry: ScheduleEntry): string | null {
  return entry.planId ?? null;
}

/**
 * Адрес формы «Задать домашнюю» с подставленными полями.
 *
 * Имена параметров те же, какими их читает форма (`AssignHomeworkPage`), и
 * пустые в адрес не кладутся: `planId=` пустой строкой форма прочитала бы как
 * выбранную заготовку и показала бы отказ вместо списка.
 *
 * ⚠️ `lessonId` это подпись «задана после занятия», и форма ставит её только
 * тому, кто выбран один. Класть его безопасно: занятие принадлежит той же паре,
 * что и ученик в адресе.
 */
export function assignHref(args: {
  studentUserId: string;
  planId: string | null;
  lessonId: string | null;
}): string {
  const p = new URLSearchParams({ studentId: args.studentUserId });
  if (args.planId !== null) p.set("planId", args.planId);
  if (args.lessonId !== null) p.set("lessonId", args.lessonId);
  return `/homework/new?${p.toString()}`;
}
